import React, {useCallback, useMemo} from 'react';
import { useSelector} from 'react-redux';
import { useTranslation } from 'react-i18next';
import PropTypes from 'prop-types';

import StatisticsTable from '../../components/StatisticsTable';
import { userTransitionsSelector, invertedTransitionsJsonSelector, transitionsJsonDatesSelector } from '../../reducers/transitions/transitionsReducer';
import { userPossibleTransitionsSelector, invertedRightsJsonSelector } from '../../reducers/rights/rightsReducer';
import { STATUSES, RIGHTS } from '../../constants';
import { dateFilter } from '../../utils/common';

const StatisticsByUser = ({ multiselectedUsers, projectsForStatistics, selectedDate: { startDate, endDate } }) => {
  const [ t ] = useTranslation();

  const { users: invertedUsers, projects: invertedProjects, states: invertedStates } = useSelector(invertedTransitionsJsonSelector);
  const { rights: invertedRights } = useSelector(invertedRightsJsonSelector);
  const userTransitions = useSelector(userTransitionsSelector);
  const userPossibleTransitions = useSelector(userPossibleTransitionsSelector);
  const datesJson = useSelector(transitionsJsonDatesSelector);

  const selectedUsersObject = useMemo(() => {
    const users = {};
    multiselectedUsers.forEach(user => {users[user] = invertedUsers[user]});

    return users;
  }, [multiselectedUsers ,invertedUsers]);

  const statesAndRightsCodes = useMemo(() => ({
    // backend returns only statuses for which it has transition
    // backend returns all rights, so no additional check needed
    [invertedStates[STATUSES.COMPLETED] || '999991']: invertedRights[RIGHTS.WRITE],
    [invertedStates[STATUSES.REVIEWED] || '999992']: invertedRights[RIGHTS.REVIEW],
    [invertedStates[STATUSES.LOCKED] || '999993']: invertedRights[RIGHTS.LOCK]
  }), [invertedStates, invertedRights]);

const statesToCount = useMemo(() => Object.keys(statesAndRightsCodes),
  [statesAndRightsCodes]
);

  /**
   * function generateTableRows
   * STATEMENT: works if user codes and project codes are the same 
   * in rights.json and transitions.json
   * statement TRUE because of backend implementation 
   * @param {object} project
   * @return {user1: {stateCode1: {transitions: {stateCount1, ...}, possibleTransitions: {rightStepCount}}, user2: ...}
   */
  const generateTableRows = useCallback(userName => {
    // const statistics = userStatistics(userName);
    const userCode = selectedUsersObject[userName];

    return projectsForStatistics.map(({ name: projectName, path, sidekick }) => {
      const projectCode = invertedProjects[path];

      return [projectName, ...statesToCount.map(state => {
        const rightCode = statesAndRightsCodes[state];

        const userStateStatistics = userTransitions[userCode] && userTransitions[userCode][projectCode] ?
          userTransitions[userCode][projectCode][state] :
          [];

        // get user transitions
        const userTransitionDates = userStateStatistics ?
          // convert num date to date object for filtering
          userStateStatistics.map(dateKey => new Date(datesJson[dateKey])) :
          [];
        const doneTransitions = dateFilter(userTransitionDates, startDate, endDate);
        
        // get user possible transitions (user has right)
        if (!userPossibleTransitions[userCode] || !userPossibleTransitions[userCode][projectCode]) {
          return <span>{`${doneTransitions}/0`}<span className="align-right">0%</span></span>;
        }

        const totalTransitions = userPossibleTransitions[userCode][projectCode][rightCode];
        const allTransitions = sidekick.total_steps;
        const progress = ((allTransitions > 0) ? (doneTransitions / allTransitions) * 100 : 0).toFixed(1);

        return <span>{doneTransitions}/{totalTransitions}<span className="align-right">{progress}%</span></span>;
      })];
    })
  }, [
    startDate,
    endDate,
    datesJson,
    invertedProjects,
    projectsForStatistics,
    selectedUsersObject,
    statesAndRightsCodes,
    statesToCount,
    userPossibleTransitions,
    userTransitions
  ]);

  const userStatisticsHeader = [
    t('statisticsTable.headers.project'),
    t('statisticsTable.headers.completed'),
    t('statisticsTable.headers.reviewed'),
    t('statisticsTable.headers.locked')
  ];

  return (
    <div className="current-steps-statistics-table">
        {multiselectedUsers.map(user => {
          return (
            <div key={user}>
              <h4>{t('common.user')}: {user}</h4>
              <StatisticsTable
                tableHeaders={userStatisticsHeader}
                tableData={generateTableRows(user)}
                />
            </div>
          );
        }
      )}
    </div>
  );
};

StatisticsByUser.propTypes = {
  multiselectedUsers: PropTypes.arrayOf(PropTypes.string),
  projectsForStatistics: PropTypes.arrayOf(PropTypes.object),
  selectedDate: PropTypes.shape({
    startDate: PropTypes.string,
    endDate: PropTypes.string
  })
};

export default StatisticsByUser;
